import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Card } from '@/components/atoms/Card';
import FormField from '@/components/atoms/FormField';
import TextArea from '@/components/atoms/TextArea';
import TextField from '@/components/atoms/TextField';
import { SuggestNameButton } from '@/components/organisms/FishGroupForms';

import type { OperateWizardAction, WizardDestination } from '../../operateWizardReducer';

import styles from '../../operateModals.module.scss';

interface RemainderCardProps {
    destination: WizardDestination;
    sourceCageName: string;
    sourceName: string;
    dispatch: Dispatch<OperateWizardAction>;
}

export function RemainderCard(props: RemainderCardProps) {
    const { destination, sourceCageName, sourceName, dispatch } = props;
    const { t } = useTranslation('fishGroups');

    return (
        <Card
            as="article"
            radius="sm"
            padding="xs"
            accent="stripe"
            className={classNames(styles.destinationCard, styles.remainderCard)}>
            <header className={styles.destinationHeader}>
                <h5 className={styles.destinationTitle}>
                    {t('actions.operate.split.remainderTitle', { cageName: sourceCageName })}
                </h5>
            </header>

            <div className={styles.fieldsGrid}>
                {/*
                  No cage selector — the remainder is pinned to the source's
                  own cage. Name spans full width so Count flows to its own
                  row, matching the canonical Name+Cage / Count / Notes
                  layout used elsewhere.
                */}
                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-name`}
                    label={t('actions.operate.destinations.fields.name')}
                    labelAction={
                        <SuggestNameButton
                            cageName={sourceCageName}
                            operation="split"
                            onApply={(value) =>
                                dispatch({
                                    type: 'setDestinationName',
                                    tempId: destination.tempId,
                                    value,
                                })
                            }
                        />
                    }
                    className={styles['field--fullWidth']}>
                    <TextField
                        id={`${destination.tempId}-name`}
                        type="text"
                        placeholder={t('actions.operate.split.remainderNamePlaceholder', {
                            sourceName,
                        })}
                        value={destination.name}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationName',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-count`}
                    label={t('actions.operate.destinations.fields.estimatedCount')}
                    hint={t('actions.operate.destinations.fields.estimatedCountHint')}>
                    <TextField
                        id={`${destination.tempId}-count`}
                        type="number"
                        min={1}
                        value={destination.estimatedFishCount}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationEstimatedCount',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-notes`}
                    label={t('actions.operate.destinations.fields.notes')}
                    className={styles['field--fullWidth']}>
                    <TextArea
                        id={`${destination.tempId}-notes`}
                        rows={2}
                        value={destination.notes}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationNotes',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>
            </div>
        </Card>
    );
}
